import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, Empty, StatusBadge, History } from "@/components/common";
import { useApi, errMsg, fmtDate, sar } from "@/lib/api";
import { MAINT_STATUS, ORDER_STATUS, URGENCY } from "@/lib/labels";
import { MaintenancePhotos, EmailStatus } from "@/components/Photos";

const Detail = ({ t, kind }) =>
  kind === "maintenance" ? (
    <>
      <p className="font-bold text-[#0F382C]">{t.service_name} <span className="text-xs font-normal text-[#5C7067]">· {URGENCY[t.urgency]}</span></p>
      <p className="text-sm text-[#5C7067]">{t.location}</p>
      <p className="mt-1 text-sm">{t.description}</p>
      <MaintenancePhotos task={t} />
    </>
  ) : (
    <>
      <p className="font-bold text-[#0F382C]">{sar(t.total)} <span className="text-xs font-normal text-[#5C7067]">(تركيب {sar(t.installation_total)}) · الدفع عند التنفيذ</span></p>
      <ul className="mt-1 text-sm">{t.items.map((i, k) => <li key={k}>{i.name} × {i.qty}{i.with_installation && " — مع التركيب"}</li>)}</ul>
      {t.notes && <p className="mt-1 text-sm text-[#5C7067]">ملاحظات: {t.notes}</p>}
    </>
  );

const TaskRow = ({ t, kind, employees, onPatch }) => {
  const map = kind === "maintenance" ? MAINT_STATUS : ORDER_STATUS;
  return (
    <div className="min-w-0 rounded-2xl border border-[#E5DEC9] bg-white p-6" data-testid={`admin-task-${t.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs text-[#5C7067]"><span className="ltr-num">{t.code}</span> · {fmtDate(t.created_at)}</p>
          <p className="text-sm font-semibold">{t.owner_name} · {t.project_name} · {t.unit}</p>
        </div>
        <StatusBadge map={map} status={t.status} testId={`admin-task-status-${t.code}`} />
      </div>
      <div className="mt-3"><Detail t={t} kind={kind} /></div>
      <EmailStatus log={t.last_email} testId={`admin-task-email-${t.code}`} />
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Select dir="rtl" value={t.assigned_to || ""} onValueChange={(v) => onPatch(t, { assigned_to: v })}>
          <SelectTrigger data-testid={`admin-task-assign-${t.code}`}><SelectValue placeholder="تعيين موظف" /></SelectTrigger>
          <SelectContent>
            {employees.length === 0 && <div className="p-2 text-sm text-[#5C7067]">لا يوجد موظفون بهذه الصلاحية</div>}
            {employees.map((e) => <SelectItem key={e.user_id} value={e.user_id}>{e.name || e.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select dir="rtl" value={t.status} onValueChange={(v) => onPatch(t, { status: v })}>
          <SelectTrigger data-testid={`admin-task-status-select-${t.code}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(map).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <details className="mt-3"><summary className="cursor-pointer text-sm font-semibold text-[#0F382C]">السجل</summary><div className="mt-3"><History items={t.history} map={map} /></div></details>
    </div>
  );
};

export default function AdminTasks({ kind }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState("open");
  const load = useCallback(() => {
    api("get", `/admin/${kind}`).then(setItems).catch((e) => toast.error(errMsg(e)));
    api("get", "/users").then((d) => setEmployees(d.users.filter((u) => u.role === "employee" && (u.permissions || []).includes(kind)))).catch(() => {});
  }, [api, kind]);
  useEffect(() => {
    load();
  }, [load]);
  const patch = async (t, body) => {
    try {
      await api("patch", `/admin/${kind}/${t.id}`, body);
      toast.success("تم التحديث");
      load();
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!items) return <Spinner />;
  const closed = ["completed", "rejected", "cancelled"];
  const shown = items.filter((t) => filter === "all" || (filter === "open" ? !closed.includes(t.status) : closed.includes(t.status)));
  return (
    <div data-testid={`admin-${kind}-tasks`}>
      <div className="mb-4 flex gap-2">
        {[["open", "مفتوحة"], ["closed", "مغلقة"], ["all", "الكل"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-${kind}-filter-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${filter === k ? "bg-[#0F382C] text-white" : "bg-white border border-[#E5DEC9]"}`}>{l}</button>
        ))}
      </div>
      {shown.length === 0 ? <Empty icon={ClipboardList} title="لا توجد طلبات" testId={`admin-${kind}-empty`} /> : (
        <div className="grid gap-4 lg:grid-cols-2">{shown.map((t) => <TaskRow key={t.id} t={t} kind={kind} employees={employees} onPatch={patch} />)}</div>
      )}
    </div>
  );
}
